import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowClockwise, Palette, Check } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import {
  COOLMAN_DEFAULT_PALETTE,
  PALETTE_LABELS,
  PALETTE_HINTS,
  contrastRatio,
  readableForeground,
  publishPaletteAsCssVariables,
} from "@/lib/designSystem";

/**
 * BrandColourPalette — DSG-006 admin surface.
 *
 * Product Owner directive 2026-07-03:
 *   Branding manages the COMPANY'S palette. The Design System derives
 *   the application theme from it. Admins never edit UI tokens
 *   directly.
 *
 * Behaviour:
 *   - Loads current overrides from /api/admin/settings/branding/theme
 *   - Live-previews the palette to :root CSS variables as the admin
 *     tweaks colours (nothing is persisted until Save)
 *   - Save → PUT /api/admin/settings/branding/theme
 *   - Restore Default → POST /api/admin/settings/branding/theme/reset
 *     (returns to the locked "Coolman Default" preset)
 *   - Contrast validation flags palette entries with < 3.0 contrast
 *     against white — cheap heuristic to prevent WCAG failures
 */

const SEMANTIC_ORDER = ["primary", "secondary", "accent", "success", "warning", "error", "information"];

export default function BrandColourPalette() {
  const { refresh: refreshBranding } = useBranding();
  const [initialPalette, setInitialPalette] = useState(COOLMAN_DEFAULT_PALETTE);
  const [palette, setPalette] = useState(COOLMAN_DEFAULT_PALETTE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/settings/branding/theme");
      const merged = { ...COOLMAN_DEFAULT_PALETTE, ...(r.data?.colors || {}) };
      setInitialPalette(merged);
      setPalette(merged);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load Brand Colour Palette");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Live preview — publish uncommitted palette to :root so the admin
  // sees the change reflected across the entire shell before saving.
  useEffect(() => {
    publishPaletteAsCssVariables(palette);
  }, [palette]);

  // On unmount, restore whatever the server currently thinks the palette
  // is so an admin who navigates away without saving doesn't leave
  // colours mismatched for other users of the same session.
  useEffect(() => {
    return () => publishPaletteAsCssVariables(initialPalette);
  }, [initialPalette]);

  const dirty = useMemo(
    () => SEMANTIC_ORDER.some((k) => (palette[k] || "").toUpperCase() !== (initialPalette[k] || "").toUpperCase()),
    [palette, initialPalette],
  );

  const setColor = (key, value) => setPalette((p) => ({ ...p, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const overrides = {};
      for (const k of SEMANTIC_ORDER) {
        const current = (palette[k] || "").toUpperCase();
        const def = COOLMAN_DEFAULT_PALETTE[k].toUpperCase();
        // Only persist overrides that differ from the default — keeps
        // the DB small and lets future default changes propagate to
        // untouched tenants.
        if (current && current !== def) overrides[k] = palette[k];
      }
      await api.put("/admin/settings/branding/theme", { colors: overrides });
      toast.success("Brand Colour Palette saved. Application theme updated.");
      await refreshBranding();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save palette");
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setPalette(initialPalette);
    toast.info("Unsaved palette changes discarded.");
  };

  const restoreDefault = async () => {
    setSaving(true);
    try {
      await api.post("/admin/settings/branding/theme/reset");
      toast.success("Restored the Coolman Default palette.");
      await refreshBranding();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not restore default palette");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-slate-200 p-5 shadow-sm">
        <div className="text-sm text-slate-500">Loading Brand Colour Palette…</div>
      </Card>
    );
  }

  return (
    <Card
      className="border-slate-200 p-5 shadow-sm"
      data-testid="brand-colour-palette"
    >
      <div className="mb-3 flex items-start justify-between gap-4">
        <div>
          <h3 className="flex items-center gap-2 text-base font-semibold text-charcoal">
            <Palette size={18} weight="bold" /> Brand Colour Palette
          </h3>
          <p className="mt-1 text-xs text-ink-secondary">
            The company&apos;s authoritative palette. The Design System derives every application
            theme colour from these values — administrators never edit UI tokens directly.
          </p>
        </div>
        <div className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-amber-800">
          Coolman Default · Locked Preset
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {SEMANTIC_ORDER.map((key) => (
          <PaletteRow
            key={key}
            token={key}
            value={palette[key]}
            defaultValue={COOLMAN_DEFAULT_PALETTE[key]}
            onChange={(v) => setColor(key, v)}
          />
        ))}
      </div>

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-divider pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={restoreDefault}
          disabled={saving}
          data-testid="palette-restore-default"
        >
          <ArrowClockwise size={14} weight="bold" className="mr-1.5" />
          Restore Coolman Default
        </Button>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={cancel}
            disabled={!dirty || saving}
            data-testid="palette-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={save}
            disabled={!dirty || saving}
            data-testid="palette-save"
          >
            {saving ? "Saving…" : "Save Palette"}
          </Button>
        </div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
function PaletteRow({ token, value, defaultValue, onChange }) {
  const label = PALETTE_LABELS[token];
  const hint = PALETTE_HINTS[token];
  const isDefault = (value || "").toUpperCase() === (defaultValue || "").toUpperCase();
  // WCAG contrast against WHITE — a fast proxy for "is this legible on
  // the app's default surface?". Anything below 3.0 gets a warning.
  const contrast = value ? contrastRatio(value, "#FFFFFF") : 0;
  const low = contrast > 0 && contrast < 3.0;
  return (
    <div
      className="rounded-md border border-divider bg-surface p-3"
      data-testid={`palette-row-${token}`}
    >
      <div className="flex items-center gap-3">
        <label className="relative block h-10 w-14 shrink-0 cursor-pointer overflow-hidden rounded border border-divider">
          <input
            type="color"
            data-testid={`palette-color-${token}`}
            value={value || "#000000"}
            onChange={(e) => onChange(e.target.value.toUpperCase())}
            className="absolute inset-0 h-full w-full cursor-pointer border-0 p-0"
          />
        </label>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-semibold text-charcoal">{label}</Label>
            {isDefault && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-ink-muted" title="Matches Coolman Default">
                <Check size={10} weight="bold" /> default
              </span>
            )}
            {low && (
              <span
                data-testid={`palette-contrast-warn-${token}`}
                className="rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-800"
                title={`Contrast against white is ${contrast.toFixed(2)}:1 (WCAG minimum is 3.0)`}
              >
                Low contrast
              </span>
            )}
          </div>
          <Input
            className="mt-1 h-8 font-mono text-xs uppercase"
            data-testid={`palette-hex-${token}`}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={defaultValue}
          />
        </div>
      </div>
      <p className="mt-1.5 text-[11px] text-ink-muted">{hint}</p>
      {/* Swatch — shows how the palette entry will look with its
          derived foreground colour. */}
      <div
        className="mt-2 flex h-7 items-center justify-center rounded text-xs font-semibold"
        style={{ backgroundColor: value, color: readableForeground(value || "#FFFFFF") }}
        data-testid={`palette-swatch-${token}`}
      >
        {label} sample
      </div>
    </div>
  );
}
